// Agency: Office workspace — ch.06: "Release, training requests and activity
// per office … Same policy/test/decision/audit modules. Agency may prepare
// tests and release eligible scopes; office owner supplies the separate
// approval."
//
// ---------------------------------------------------------------------------
// "Same modules" is taken literally: this screen does not reimplement them
// ---------------------------------------------------------------------------
//
// The gate controls already exist. Working rules carries the four release
// gates, with the two an agency operator may work — record test evidence, and
// release — behind the role check the API enforces anyway. A second set of
// those forms here would be two implementations of one procedure, and the day
// they drift, the one an operator happens to be looking at decides what gets
// released.
//
// So this screen READS the office's gate state and its activity, and to ACT it
// sends the operator into Working rules in that office. That is the same
// modules, rather than a second copy of them.
//
// ---------------------------------------------------------------------------
// The one control that changes the workspace, and it says so
// ---------------------------------------------------------------------------
//
// Every other agency screen leaves the header alone: the office being examined
// lives in App's state, so moving through a portfolio does not repoint the
// customer screens behind it. Opening Working rules cannot work that way —
// Working rules reads the workspace's office, because it is an office screen.
//
// V03 forbids the context changing ACCIDENTALLY, not deliberately. So the
// control names the office it will switch to, and the button says it switches.
// An operator who presses it knows the header moved; nothing moved it for them.
//
// ---------------------------------------------------------------------------
// Training requests
// ---------------------------------------------------------------------------
//
// ch.06 lists them and nothing in this system records one: there is no table,
// no endpoint and no job family for a training request, and the nearest thing —
// a counterparty classification — is a decision about one supplier rather than
// a request to teach anything. So the card says so instead of offering a form
// that would write nowhere.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { Administration, AuditPage, Membership, PolicyVersions } from '../api/types.ts';
import { agencyMemberships, describeOfficeFailure } from '../agency.ts';
import type { ScreenId } from '../components/Shell.tsx';
import { auditAction } from '../states.ts';
import { useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';

/** Where each policy version has got to, in the endpoint's own words. */
const GATE_LABELS: Readonly<Record<string, string>> = {
  test_evidence: 'Waiting for your test evidence',
  office_approval: 'Waiting for the office owner',
  release: 'Waiting for you to release it',
  released: 'Released',
};

interface OfficeDetail {
  readonly administrations: readonly Administration[];
  readonly policies: PolicyVersions['policyVersions'];
  readonly activity: AuditPage['events'];
  /** Set when the audit read was refused rather than empty. */
  readonly activityRefusal: string | null;
}

export interface AgencyWorkspaceScreenProps {
  /** The office chosen on the Offices screen, or null if none has been. */
  readonly tenantId: string | null;
  readonly onSelectOffice: (tenantId: string) => void;
  readonly onNavigate: (screen: ScreenId) => void;
}

export function AgencyWorkspaceScreen({
  tenantId,
  onSelectOffice,
  onNavigate,
}: AgencyWorkspaceScreenProps): ReactNode {
  const workspace = useWorkspace();
  const { token } = workspace;
  const offices = agencyMemberships(workspace.me);

  // Default to the first office in the portfolio rather than to the header's
  // office: the header may be pointing at a customer workspace this screen has
  // nothing to say about.
  const selected =
    offices.find((office) => office.tenantId === tenantId) ?? offices[0] ?? null;

  const [detail, setDetail] = useState<OfficeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const officeId = selected?.tenantId ?? null;

  const load = useCallback(async () => {
    if (officeId === null) return;
    setBusy(true);
    setError(null);
    // Cleared before the next office's answer arrives, not after: one office's
    // releases under another office's name is the whole failure ch.06 warns
    // about on the portfolio screen, one level in.
    setDetail(null);
    try {
      const administrations = await request<{ administrations: Administration[] }>(
        tenantPath(officeId, '/administrations'),
        { token },
      );
      const policies = await request<PolicyVersions>(
        tenantPath(officeId, '/policy-versions'),
        { token },
      );

      // The audit log is owners and agency operators only, and a 403 here is a
      // fact about this session rather than a failure of the screen — so it is
      // carried as its own state instead of blanking the gates beside it.
      let activity: AuditPage['events'] = [];
      let activityRefusal: string | null = null;
      try {
        const page = await request<AuditPage>(tenantPath(officeId, '/audit?limit=15'), { token });
        activity = page.events;
      } catch (caught) {
        activityRefusal =
          caught instanceof ApiError && caught.status === 403
            ? 'This session may not read this office’s activity.'
            : describeOfficeFailure(caught);
      }

      setDetail({
        administrations: administrations.administrations,
        policies: policies.policyVersions,
        activity,
        activityRefusal,
      });
    } catch (caught) {
      setError(describeOfficeFailure(caught));
    } finally {
      setBusy(false);
    }
  }, [officeId, token]);

  useEffect(() => {
    void load();
  }, [load]);

  if (selected === null) {
    return (
      <>
        <div className="heading">
          <h1>Office workspace</h1>
          <p>This account operates no office.</p>
        </div>
        <div className="card">
          <div className="empty">
            <h3>No portfolio</h3>
            <p>
              An office workspace is opened for an office where you hold an agency operator
              membership. This account holds none.
            </p>
          </div>
        </div>
      </>
    );
  }

  const released = (detail?.policies ?? []).filter((policy) => policy.gates.release !== null);
  const waitingOnOperator = (detail?.policies ?? []).filter(
    (policy) => policy.gates.next === 'test_evidence' || policy.gates.next === 'release',
  );
  const waitingOnOffice = (detail?.policies ?? []).filter(
    (policy) => policy.gates.next === 'office_approval',
  );

  return (
    <>
      <div className="heading">
        <h1>Office workspace</h1>
        <p>
          One office at a time, read in that office's own context. Everything below is the same
          record the office sees on its own screens.
        </p>
      </div>

      <div className="toolbar">
        <label className="field" style={{ margin: 0, minWidth: 260 }}>
          Office
          <select
            aria-label="Select office"
            value={selected.tenantId}
            onChange={(event) => onSelectOffice(event.target.value)}
          >
            {offices.map((office) => (
              <option key={office.tenantId} value={office.tenantId}>
                {office.tenantName}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn" disabled={busy} onClick={() => void load()}>
          {busy ? 'Reading…' : 'Refresh'}
        </button>
      </div>

      {error !== null ? (
        <div className="card">
          <div className="card-body">
            <p className="callout warn">{error}</p>
          </div>
        </div>
      ) : detail === null ? (
        <div className="card">
          <div className="empty">
            <h3>Reading {selected.tenantName}</h3>
          </div>
        </div>
      ) : (
        <>
          <div className="metrics">
            <Metric
              label="Administrations"
              value={detail.administrations.length}
              caption="Granted to you in this office"
            />
            <Metric
              label="Waiting on you"
              value={waitingOnOperator.length}
              caption="Test evidence to record, or a scope to release"
            />
            <Metric
              label="Waiting on the office"
              value={waitingOnOffice.length}
              caption="The owner's written approval, which you cannot give"
            />
            <Metric label="Released scopes" value={released.length} caption="None of them runs by itself" />
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Automation policies</h2>
                <p>Where each scope has got to, in {selected.tenantName}</p>
              </div>
            </div>

            {detail.policies.length === 0 ? (
              <div className="empty">
                <h3>No policy requested</h3>
                <p>
                  This office has asked for no automation scope. The request is the office owner's
                  to make — you cannot make it for them.
                </p>
              </div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Scope</th>
                      <th>Version</th>
                      <th>Where it has got to</th>
                      <th>Bound to</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.policies.map((policy) => (
                      <tr key={policy.id} data-testid={`policy-${policy.id}`}>
                        <td>{policy.scope.replace(/_/g, ' ')}</td>
                        <td>v{policy.version}</td>
                        <td>
                          <span className="badge">
                            {GATE_LABELS[policy.gates.next] ?? policy.gates.next}
                          </span>
                        </td>
                        <td>
                          {policy.gates.release === null ? (
                            <span className="muted">—</span>
                          ) : (
                            <small>
                              {policy.gates.release.connectorVersion}
                              {policy.gates.release.modelVersion === null
                                ? ''
                                : ` · ${policy.gates.release.modelVersion}`}
                              {/* The API decides this word, never the screen. */}
                              {policy.gates.release.inForce ? '' : ' · not in force'}
                            </small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="card-foot">
              <p style={{ marginBottom: 10 }}>
                {/* The one control that moves the header. See the note at the
                    top of the file: deliberate, named, and pressed on purpose. */}
                Recording test evidence and releasing a scope happen on Working rules, which is the
                same screen the office uses. Opening it switches your workspace to{' '}
                <b>{selected.tenantName}</b>.
              </p>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  workspace.selectTenant(selected.tenantId);
                  onNavigate('rules');
                }}
              >
                Switch to {selected.tenantName} and open Working rules
              </button>
            </div>
          </div>

          <div className="card section-space">
            <div className="card-head">
              <div>
                <h2>Activity</h2>
                <p>The office's own audit record, newest first</p>
              </div>
            </div>
            {detail.activityRefusal !== null ? (
              <div className="card-body">
                <p className="callout warn">{detail.activityRefusal}</p>
              </div>
            ) : detail.activity.length === 0 ? (
              <div className="empty">
                <h3>Nothing recorded yet</h3>
                <p>No decision has been taken in the administrations you were granted.</p>
              </div>
            ) : (
              <div className="activity">
                {detail.activity.map((event) => (
                  <div className="event" key={event.id} data-testid={`event-${event.id}`}>
                    <div className="event-icon" />
                    <div>
                      <b>{auditAction(event.action).label}</b>
                      <p>
                        {event.actorKind} · {event.targetKind}
                      </p>
                      <time>{format.dateTime(event.occurredAt)}</time>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <div className="card section-space">
        <div className="card-head">
          <h2>Training requests</h2>
        </div>
        <div className="card-body">
          <p className="callout">
            {/* ch.06 lists them; nothing records one. A form here would write
                nowhere, and V07 asks for a real workflow or nothing. */}
            Nothing in this system records a training request. There is no table, no endpoint and
            no job family for one, and the nearest record — a counterparty classification — is a
            decision about one supplier rather than a request to teach anything. So there is no
            form here rather than one that would go nowhere.
          </p>
        </div>
      </div>

      <div className="card section-space">
        <div className="card-head">
          <h2>What you may do here, and what you may not</h2>
        </div>
        <div className="card-body">
          <ul className="note-list">
            <li>
              <b>You cannot approve on the office's behalf.</b> ch.02: the agency operator "records
              test/release evidence but does not impersonate the office approver". A role is per
              membership, so no session can hold both sides — the API does not check that the
              approver is not the tester, because it cannot be.
            </li>
            <li>
              <b>A release authorises nothing here.</b> Nothing in this build consults one, so
              every proposal still waits for a person. The API says so on each release and this
              screen repeats it rather than translating "released" into "automatic".
            </li>
            <li>
              <b>You see the administrations this office granted you.</b> ch.02: agency support has
              no standing unrestricted invoice access.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function Metric({
  label,
  value,
  caption,
}: {
  readonly label: string;
  readonly value: number;
  readonly caption: string;
}): ReactNode {
  return (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className="caption">{caption}</div>
    </div>
  );
}
